import { useCallback, useEffect, useState, type CSSProperties } from "react";
import QRCode from "qrcode";
import type { SlideProps } from "@/engine/app/offsite-config";
import { useOffsiteConfig } from "@/engine/app/offsite-context";
import { teamLabel } from "@/engine/app/team-label";
import { useStore } from "@/state/store";
import { api } from "@/api/client";

/**
 * Team-QR-Folie: vier Codes nebeneinander, jedes Team scannt seinen.
 *
 * Die Codes sind mehrfach einlösbare Team-Codes (POST /admin/codes mit
 * `team: n`) — der Scan loggt direkt ins feste Team ein, ohne Tippen. Die
 * Folie liest sie über die Presenter-Session aus der Codes-Liste; fehlen
 * welche, legt der Moderator sie mit einem Klick direkt hier an (der Knopf
 * erscheint nur, solange Codes fehlen — vor Publikum ist er weg).
 *
 * Unter jedem QR steht der eingetragene Teamname, sobald das Team ihn am
 * Handy gesetzt hat — so sieht der Raum live, dass der Login funktioniert.
 * Darunter „n eingeloggt": der Einlösungszähler des Team-Codes (Presenter
 * pollt alle 4 s). Gezählt werden Logins, nicht Geräte — scannt ein Handy
 * erneut, zählt es doppelt; Test-Logins vor dem Event bleiben im Zähler.
 */

const CODES_POLL_MS = 4000;

const WORKSHOP_URL = import.meta.env.VITE_WORKSHOP_URL as string | undefined;

/**
 * Eine Akzentfarbe je Team — verhindert, dass jemand den falschen scannt.
 * Alle aus dem SVO-Styleguide (design-system tokens.css): Blau, Grün,
 * Warning-Gelb; die drei Untergruppen von Gruppe 4 (4a/4b/4c) teilen sich
 * die Weiß/Grau-Familie. Dunkelblau entfällt (unsichtbar auf dunklem
 * Grund), Error-Rot ebenso (würde als Fehlermeldung gelesen).
 */
export const TEAM_FARBEN = [
  "#008DF7",
  "#79D97C",
  "#F5A623",
  "#FFFFFF",
  "#BDBDBD",
  "#757575",
];

function loginUrl(code: string): string {
  const base = WORKSHOP_URL ?? window.location.origin;
  const u = new URL(base);
  u.pathname = "/m/login";
  u.search = "";
  u.searchParams.set("p", code);
  return u.toString();
}

function TeamQr({
  code,
  farbe,
  scharf,
}: {
  code: string;
  farbe: string;
  /** Nur der aufgerufene QR ist scharf — die anderen bleiben unscannbar verpixelt. */
  scharf: boolean;
}) {
  const [svg, setSvg] = useState<string | null>(null);
  useEffect(() => {
    QRCode.toString(loginUrl(code), {
      type: "svg",
      errorCorrectionLevel: "M",
      margin: 1,
      color: { dark: "#00103a", light: "#FFFFFF" },
    })
      .then(setSvg)
      .catch(() => setSvg(null));
  }, [code]);

  return (
    <div
      aria-hidden="true"
      style={{
        background: "#fff",
        borderRadius: 16,
        border: `6px solid ${farbe}`,
        padding: 14,
        boxSizing: "border-box",
        aspectRatio: "1 / 1",
        // Kantenlänge aus der Folie (--nw-qr, siehe Slide13Teams) — hängt an
        // Breite UND Höhe, nicht nur an der Spaltenbreite.
        width: "var(--nw-qr, 100%)",
        position: "relative",
        overflow: "hidden",
      }}
    >
      {svg ? (
        <span
          style={{
            display: "block",
            width: "100%",
            height: "100%",
            // Unscharf ist unscannbar: erst der Aufruf („Team 1!") macht den
            // Code lesbar — so scannt niemand versehentlich das falsche Team.
            filter: scharf ? "none" : "blur(16px)",
            transition: "filter 300ms ease",
          }}
          dangerouslySetInnerHTML={{ __html: svg }}
        />
      ) : (
        <div style={{ display: "grid", placeItems: "center", height: "100%" }}>
          …
        </div>
      )}
      {!scharf && svg && (
        <div
          style={{
            position: "absolute",
            inset: 0,
            display: "grid",
            placeItems: "center",
            fontSize: 44,
            color: "rgba(0,16,58,0.55)",
          }}
        >
          🔒
        </div>
      )}
    </div>
  );
}

export function Slide13Teams(_: SlideProps) {
  const { festeTeams } = useOffsiteConfig();
  const anzahl = festeTeams ?? 4;
  const { state } = useStore();
  const token = state.auth.token;
  const istPresenter = state.auth.role === "presenter";

  /** Teamnummer → Code. */
  const [codes, setCodes] = useState<Map<number, string>>(new Map());
  /** Teamnummer → Zahl der Logins mit dem Team-Code. */
  const [logins, setLogins] = useState<Map<number, number>>(new Map());
  /** Teamnummer → eingetragener Teamname. */
  const [namen, setNamen] = useState<Map<number, string>>(new Map());
  const [creating, setCreating] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);
  /**
   * Nur ein QR ist gleichzeitig scharf: Der Moderator ruft „Team 1!" auf,
   * klickt dessen Karte, das Team scannt, nächster Klick wechselt (oder
   * derselbe blurrt wieder). Verhindert Scans ins falsche Team.
   */
  const [scharfesTeam, setScharfesTeam] = useState<number | null>(null);

  const ladeCodes = useCallback(async () => {
    if (!istPresenter) return;
    try {
      const r = await api.admin.listCodes(token);
      const next = new Map<number, string>();
      const zaehler = new Map<number, number>();
      for (const c of r.codes) {
        if (c.mehrfach && c.teamNumber) {
          if (!next.has(c.teamNumber)) next.set(c.teamNumber, c.code);
          // Über alle Team-Codes derselben Nummer summieren — falls je ein
          // zweiter angelegt wurde, zählt dessen Login genauso ins Team.
          zaehler.set(
            c.teamNumber,
            (zaehler.get(c.teamNumber) ?? 0) + (c.einloesungen ?? 0),
          );
        }
      }
      setCodes(next);
      setLogins(zaehler);
    } catch {
      /* noop — nächster Tick */
    }
  }, [token, istPresenter]);

  // Codes + Login-Zähler live: der Moderator sieht, welcher QR noch
  // gebraucht wird.
  useEffect(() => {
    void ladeCodes();
    const id = setInterval(() => void ladeCodes(), CODES_POLL_MS);
    return () => clearInterval(id);
  }, [ladeCodes]);

  // Teamnamen live nachladen: Sobald ein Team seinen Namen einträgt,
  // erscheint er unter dem QR — sichtbares Feedback für den Raum.
  useEffect(() => {
    let cancelled = false;
    const lade = async () => {
      try {
        const r = await api.teams.list(token);
        if (cancelled) return;
        const next = new Map<number, string>();
        for (const t of r.teams) {
          if (t.name) next.set(t.number, t.name);
        }
        setNamen(next);
      } catch {
        /* noop */
      }
    };
    void lade();
    const id = setInterval(lade, 5000);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [token]);

  const fehlende = Array.from({ length: anzahl }, (_, i) => i + 1).filter(
    (n) => !codes.has(n),
  );
  // Ab fünf Teams zwei Reihen — alles etwas kompakter, damit beide Reihen
  // samt Überschrift auf die Leinwand passen.
  const zweiReihen = anzahl > 4;
  const spalten = zweiReihen ? Math.ceil(anzahl / 2) : anzahl;
  const reihen = zweiReihen ? 2 : 1;

  // Zeilenhöhen fest in px, Texte einzeilig: Nur so ist die Höhe einer
  // Karte ohne QR exakt bekannt, und die QR-Kante lässt sich rein in CSS aus
  // der freien Höhe ableiten (siehe --nw-qr unten).
  const m = zweiReihen
    ? { label: 24, labelLh: 28, name: 16, nameLh: 20, zaehler: 15, zaehlerLh: 18, kartenGap: 6, zeilenGap: 12, spaltenGap: 44 }
    : { label: 32, labelLh: 40, name: 22, nameLh: 30, zaehler: 20, zaehlerLh: 26, kartenGap: 16, zeilenGap: 48, spaltenGap: 48 };
  // Der Zähler erscheint nur am Pult; die Zuschauer-Ansicht bekommt den
  // Platz für größere Codes.
  const mitZaehler = istPresenter;
  const kartenRest =
    m.labelLh + m.kartenGap + m.kartenGap + m.nameLh +
    (mitZaehler ? m.kartenGap + m.zaehlerLh : 0);
  // QR-Kante = kleinere von zwei Grenzen, gemessen am Raster-Container
  // (container-type: size → cqw/cqh): Spaltenbreite und Reihenhöhe minus
  // Beschriftung. Vorher hing die Kante nur an der (festen) Breite und lief
  // auf niedrigen Bildschirmen unter die Fortschrittsleiste.
  const qrKante =
    `min(` +
    `(100cqw - ${(spalten - 1) * m.spaltenGap}px) / ${spalten}, ` +
    `(100cqh - ${(reihen - 1) * m.zeilenGap}px) / ${reihen} - ${kartenRest}px` +
    `)`;
  const einzeilig = {
    maxWidth: "100%",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  } as const;

  const legeCodesAn = async () => {
    setCreating(true);
    setFehler(null);
    try {
      for (const n of fehlende) {
        await api.admin.createTeamCode(token, n, teamLabel(n));
      }
      await ladeCodes();
    } catch {
      setFehler("Codes konnten nicht angelegt werden — nochmal versuchen.");
    } finally {
      setCreating(false);
    }
  };

  return (
    // Äußere Hülle = Größen-Container der Folie: Überschrift und Abstände
    // skalieren mit der Folienhöhe (cqh), damit auch 1440×900 passt.
    <div
      style={{
        width: "100%",
        height: "100%",
        containerType: "size",
        background: "var(--ink-900, #00103a)",
        color: "var(--ink-100, #e8edf7)",
      }}
    >
    <div
      style={{
        width: "100%",
        height: "100%",
        boxSizing: "border-box",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: zweiReihen ? "clamp(8px, 1.6cqh, 20px)" : "clamp(16px, 3.5cqh, 48px)",
        // Unten 100 px frei: Dort liegen Fortschrittsleiste und Kapitel-Pills
        // (≈ 91 px) über der Folie.
        padding: zweiReihen
          ? "clamp(10px, 2cqh, 28px) 64px 100px"
          : "clamp(16px, 4cqh, 48px) 64px 100px",
      }}
    >
      <div style={{ textAlign: "center", flex: "none" }}>
        <div
          style={{
            fontSize: zweiReihen ? "clamp(13px, 1.6cqh, 20px)" : "clamp(15px, 1.9cqh, 20px)",
            letterSpacing: "0.2em",
            textTransform: "uppercase",
            opacity: 0.7,
          }}
        >
          Gruppenarbeit
        </div>
        <div
          style={{
            fontSize: zweiReihen ? "clamp(26px, 3.3cqh, 40px)" : "clamp(36px, 5.2cqh, 56px)",
            lineHeight: 1.15,
            fontWeight: 700,
            marginTop: zweiReihen ? "clamp(2px, 0.6cqh, 8px)" : 8,
          }}
        >
          Scannt den Code eures Teams
        </div>
        <div
          style={{
            fontSize: zweiReihen ? "clamp(13px, 1.5cqh, 16px)" : "clamp(16px, 1.9cqh, 20px)",
            marginTop: zweiReihen ? "clamp(2px, 0.5cqh, 6px)" : 10,
            opacity: 0.65,
          }}
        >
          Die Codes werden nacheinander freigegeben — wartet, bis euer Team
          aufgerufen wird.
        </div>
      </div>

      {/* Rasterfläche = Rest der Folienhöhe; eigener Größen-Container, aus
       *  dem --nw-qr die QR-Kante berechnet. */}
      <div
        style={{
          flex: "1 1 0",
          minHeight: 0,
          width: "100%",
          containerType: "size",
          display: "grid",
          alignContent: "center",
        }}
      >
      <div
        style={
          {
            "--nw-qr": qrKante,
            display: "grid",
            gridTemplateColumns: `repeat(${spalten}, minmax(0, 1fr))`,
            gap: `${m.zeilenGap}px ${m.spaltenGap}px`,
            width: "100%",
          } as CSSProperties
        }
      >
        {Array.from({ length: anzahl }, (_, i) => i + 1).map((n) => {
          const farbe = TEAM_FARBEN[(n - 1) % TEAM_FARBEN.length];
          const code = codes.get(n);
          const name = namen.get(n);
          const scharf = scharfesTeam === n;
          return (
            <button
              key={n}
              type="button"
              onClick={() => setScharfesTeam((s) => (s === n ? null : n))}
              title={scharf ? `${teamLabel(n)} wieder verdecken` : `${teamLabel(n)} freigeben`}
              style={{
                all: "unset",
                cursor: "pointer",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                minWidth: 0,
                gap: m.kartenGap,
                opacity: scharfesTeam === null || scharf ? 1 : 0.55,
                transition: "opacity 300ms ease",
              }}
            >
              <div
                style={{
                  fontSize: m.label,
                  lineHeight: `${m.labelLh}px`,
                  fontWeight: 700,
                  color: farbe,
                  letterSpacing: "0.04em",
                  ...einzeilig,
                }}
              >
                {teamLabel(n)}
              </div>
              {code ? (
                <TeamQr code={code} farbe={farbe} scharf={scharf} />
              ) : (
                <div
                  style={{
                    width: "var(--nw-qr, 100%)",
                    boxSizing: "border-box",
                    aspectRatio: "1 / 1",
                    borderRadius: 16,
                    border: `3px dashed ${farbe}`,
                    display: "grid",
                    placeItems: "center",
                    opacity: 0.6,
                    fontSize: 18,
                  }}
                >
                  Code fehlt
                </div>
              )}
              <div
                style={{
                  fontSize: m.name,
                  lineHeight: `${m.nameLh}px`,
                  height: m.nameLh,
                  opacity: name ? 1 : 0.45,
                  fontWeight: name ? 600 : 400,
                  ...einzeilig,
                }}
              >
                {name ?? "— noch kein Name —"}
              </div>
              {istPresenter && code && (
                <div
                  aria-live="polite"
                  style={{
                    fontSize: m.zaehler,
                    lineHeight: `${m.zaehlerLh}px`,
                    height: m.zaehlerLh,
                    fontVariantNumeric: "tabular-nums",
                    ...einzeilig,
                    opacity: (logins.get(n) ?? 0) > 0 ? 0.85 : 0.5,
                  }}
                >
                  {logins.get(n) ?? 0} eingeloggt
                </div>
              )}
            </button>
          );
        })}
      </div>
      </div>

      {istPresenter && fehlende.length > 0 && (
        <div style={{ display: "grid", gap: 8, justifyItems: "center" }}>
          <button
            type="button"
            onClick={() => void legeCodesAn()}
            disabled={creating}
            style={{
              fontSize: 18,
              padding: "12px 28px",
              borderRadius: 10,
              border: "none",
              cursor: creating ? "wait" : "pointer",
              background: "#fff",
              color: "#00103a",
              fontWeight: 600,
            }}
          >
            {creating ? "…" : `Team-Codes anlegen (${fehlende.length} fehlen)`}
          </button>
          {fehler && (
            <div role="alert" style={{ color: "#F2994A", fontSize: 16 }}>
              {fehler}
            </div>
          )}
        </div>
      )}
    </div>
    </div>
  );
}
